'use client'

import { Suspense, useContext, useEffect, useRef } from 'react'
import { Cell, Divider, Navigation, Text } from '@vinted/web-ui'

import { Advertisement } from '@marketplace-web/ads/ads-feature'
import { AdShape } from '@marketplace-web/ads/ads-data'
import { ShowAtBreakpoint, useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { InfiniteScroll } from '@marketplace-web/common-components/infinite-scroll-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { INBOX_URL } from '@marketplace-web/messaging/navigation-feature'
import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'

import {
  useConversationContext,
  ConversationListContext,
  ConversationListItemType,
} from '@marketplace-web/messaging/conversation-providers-feature'

import ConversationListItem from '../ConversationListItem'
import ConversationListEmptyState from '../ConversationListEmptyState'
import TrackMessagingViewEvent from '../TrackMessagingViewEvent'

const ConversationList = () => {
  const { clearConversation } = useConversationContext()
  const translate = useTranslate('conversations.list')
  const infiniteScrollWrapRef = useRef<HTMLDivElement>(null)
  const breakpoints = useBreakpoint()
  const { uiState, conversations, isEndReached, setCurrentPage } =
    useContext(ConversationListContext)
  const { relativeUrl } = useBrowserNavigation()

  const isInboxPage = relativeUrl === INBOX_URL
  const linkTracking = { target: 'merge_message_list_banner' } as const

  const handlePageEnd = () => {
    if (!isEndReached) {
      setCurrentPage(prevCurrentPage => prevCurrentPage + 1)
    }
  }

  const renderEmptyState = () => {
    return (
      <ShowAtBreakpoint breakpoint="portables">
        <ConversationListEmptyState />
      </ShowAtBreakpoint>
    )
  }

  const renderAd = () => {
    return (
      <Suspense key="ad">
        <div
          key="slot-inbetween"
          className="conversation-list__ad"
          data-testid="slot-container"
          suppressHydrationWarning
        >
          <Advertisement shape={AdShape.Inbetween} />
        </div>
      </Suspense>
    )
  }

  const renderConversation = (conversation: ConversationListItemType) => {
    return <ConversationListItem key={conversation.id} conversation={conversation} />
  }

  const renderItem = (item: ConversationListItemType | 'ad') => {
    switch (item) {
      case 'ad':
        return renderAd()
      default:
        return renderConversation(item)
    }
  }

  const renderItems = () => {
    if (!conversations.length) return renderEmptyState()

    const items: Array<ConversationListItemType | 'ad'> = [...conversations]

    if (breakpoints.portables) {
      items.splice(3, 0, 'ad')
    }

    return (
      <SeparatedList separator={<Divider testId="conversation-list-divider" />}>
        {items.map(renderItem)}
      </SeparatedList>
    )
  }

  const renderToolbar = () => (
    <div className="conversation-list-toolbar">
      <Navigation
        left={
          <Cell styling="narrow">
            <div className="u-ui-padding-left-small">
              <Text as="h2" type="title" text={translate('title')} />
            </div>
          </Cell>
        }
      />
    </div>
  )

  const renderInfiniteScroll = () => {
    return (
      <>
        <Divider />
        <div className="u-fill-height u-overflow-auto" ref={infiniteScrollWrapRef}>
          <InfiniteScroll renderItems={renderItems} onPageEnd={handlePageEnd} />
        </div>
      </>
    )
  }

  const renderContent = () => {
    if (uiState === UiState.Pending) return <ContentLoader testId="loader" />

    return (
      <>
        <TrackMessagingViewEvent screen="inbox" />
        {renderToolbar()}
        {renderInfiniteScroll()}
      </>
    )
  }

  const renderInfoBanner = () => {
    return <InfoBanner screen="message_list" linkTracking={linkTracking} />
  }

  useEffect(() => {
    if (!breakpoints.portables) return
    if (!isInboxPage) return

    clearConversation()
  }, [clearConversation, breakpoints, isInboxPage])

  return (
    <>
      {renderInfoBanner()}
      {renderContent()}
    </>
  )
}

export default ConversationList
